import { WidgetSettings } from '@gsc/types'
import { snake_case } from '../../shared/strings'
import { ConfigBase } from './config'
import { Event } from './event'

export class GAConfig extends ConfigBase {
  public category: string = RUNTIME_KEY

  constructor(config?: any) {
    // true/false
    // future: tracker name, default/all trackers, event types
    super(config)
  }

  public isWidgetEnabled(widget: Partial<WidgetSettings>): boolean {
    const widgetEnabled = (widget.settings || {}).trackGA
    return widgetEnabled !== false && this.isEnabled() && (this.isEnabledDefault() || !!widgetEnabled)
  }

  // see https://developers.google.com/analytics/devguides/migration/measurement/events
  protected getQueue(): (e: Event) => void {
    const gao = (window as any).GoogleAnalyticsObject || 'ga'
    const ga = (window as any)[gao]
    const gtag = (window as any).gtag

    if (ga && typeof ga === 'function') {
      const cmdList: string[] = []
      if (typeof ga.getAll === 'function') {
        // collect all trackers, ex: gtm create non default tracker for GA
        const trackerIds = new Set<string>()
        ga.getAll().forEach((t: any) => {
          const [id, name] = [t.get('trackingId'), t.get('name')]
          if (!trackerIds.has(id)) {
            cmdList.push(`${name}.send`)
            trackerIds.add(id)
          }
        })
      }

      if (cmdList.length === 0) {
        cmdList.push('send') // send to default tracker
      }

      return (e: Event) => cmdList.forEach(cmd => ga(cmd, this.transform(e)))
    } else if (gtag && typeof gtag === 'function') {
      return (e: Event) => {
        gtag('event', `${this.category}_${snake_case(e.type)}`, {
          category: this.category,
          sender_name: e.widget?.name.substring(0, 100), // see https://support.google.com/analytics/answer/9267744
          sender_id: e.widget?.id,
        })
      }
    } else {
      // deprecated ga.js support
      // https://developers.google.com/analytics/devguides/collection/gajs/methods/gaJSApiEventTracking#_gat.GA_EventTracker_._trackEvent
      const _gaq = (window as any)._gaq
      if (_gaq && typeof _gaq.push === 'function') {
        return (e: Event) => {
          const gae = this.transform(e)
          _gaq.push([
            '_trackEvent',
            gae.eventCategory,
            gae.eventAction,
            gae.eventLabel,
            gae.eventValue,
            gae.nonInteraction,
          ])
        }
      }
    }
  }

  private transform(e: Event): any {
    return {
      hitType: 'event',
      eventCategory: this.category,
      eventAction: e.type,
      eventLabel: e.widget ? `${e.widget.name} #${e.widget.id}` : undefined,
      eventValue: undefined,
      nonInteraction: !e.isAction(),
    }
  }
}
